import React, { useEffect } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomRadio from "../../../../components/common/CustomRadio/CustomRadio";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import CustomTextArea from "../../../../components/common/CustomTextArea/CustomTextArea";
import BodyHeader from "../../../../components/common/CommonBodyHeader";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
   addDriverMasterAsync,
   fetchAllContractorsAsync,
} from "../../../../redux/features/driverMaster";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import {
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
   isValidFileType,
} from "../../../../helpers/fileValidationRules";
import { FormikProvider, useFormik } from "formik";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import moment from "moment";
import validation from "../../../../helpers/validation";
import useFocusErrorField from "../../../../Hooks/useFocusErrorField";
import { YRS_OF_EXPERIENCE_DATA } from "../../../../constansts/Options";

const validationSchema = Yup.object().shape({
   name: validation.name(),
   mobile: validation.mobile,
   dob: Yup.string()
      .required("DOB is required")
      .test("is-past", "Date must be in the past", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment(), "years");
      }),

   joiningDate: Yup.string()
      .required("Date of Joining is required")
      .test("is-before-2999", "Date must be before 31-12-2999", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment("2999-12-31"), "years");
      }),
   exitDate: Yup.string()
      .notRequired()
      .test("is-before-2999", "Date must be before 31-12-2999", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         if (!value) return true;
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment("2999-12-31"), "years");
      }),

   expofyears: Yup.number().required("Experience is required"),
   driverImage: Yup.mixed()
      .required("Driver Image is required")
      .test("is-valid-type", "Not a valid image type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "image")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   address: Yup.string().required("Address is required"),

   drivingLicense: Yup.string().required("Name is required"),
   licenseExp: Yup.date().required("Date Of Birth is required"),
   dlUpload: validation.file("Driver License Document", true, "all"),

   aadharCard: validation.aadharNo,
   aadharCardImg: validation.file("Aadhar card", true, 'all'),
   panCard: Yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invaid pan card no!"),
   panCardImg: validation.file("Pand card", false, 'all'),

   payRollType: Yup.string().required("Payroll Type is required"),

   contractorName: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: (schema) => schema.required("Contractor Name is required"),
      otherwise: (schema) => schema.notRequired(),
   }),
   monthlyCommAmount: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.amount("monthly commission"),
      otherwise: () => validation.amount("monthly commission", false),
   }),

   accountNumber: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.accountNumber(false),
      otherwise: () => validation.accountNumber(),
   }),
   bankName: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.bankName(false),
      otherwise: () => validation.bankName(),
   }),
   ifscCode: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.ifscCode(false),
      otherwise: () => validation.ifscCode(),
   }),
   accountHolderName: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.name("account holder name", false),
      otherwise: () => validation.name("account holder name"),
   }),
   monthlyAmount: Yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.amount("monthly salary", false),
      otherwise: () => validation.amount("monthly salary"),
   }),
});

const initialValues = {
   name: "",
   mobile: "",
   dob: "",
   joiningDate: "",
   exitDate: "",
   expofyears: "",
   driverImage: null,
   address: "",

   drivingLicense: "",
   licenseExp: "",
   dlUpload: "",

   aadharCard: "",
   aadharCardImg: null,
   panCard: "",
   panCardImg: null,

   payRollType: "Eisaku Pay Roll",

   contractorName: "",
   monthlyCommAmount: "",

   accountNumber: "",
   bankName: "",
   ifscCode: "",
   accountHolderName: "",
   monthlyAmount: "",
};

export default function DriverMasterAdd() {
   const dispatch = useDispatch();
   const navigate = useNavigate();

   const driverMaster = useSelector((state) => state.driverMaster);
   const contractorsData = driverMaster?.contractors?.map?.((item) => ({
      label: item?.name,
      value: item?.id,
   }));

   const handleAddDriverMaster = (formData) => {
      const data = {
         name: formData.name,
         mobile: formData.mobile,
         dob: formData.dob,
         doj: formData.joiningDate,
         doe: formData.exitDate,
         experience: formData.expofyears,
         driver_image: formData.driverImage,
         address: formData.address,

         driver_license: formData.drivingLicense,
         license_expiry_date: formData.licenseExp,
         dl_document: formData.dlUpload,

         aadhar_card: formData.aadharCard,
         aadhar_card_doc: formData.aadharCardImg,

         pan_card: formData.panCard,
         pan_card_doc: formData.panCardImg,

         payroll: formData.payRollType,
         model_id: 4,
         action_id: 1,
      };

      if (data.payroll === "Eisaku Pay Roll") {
         data.account_number = formData.accountNumber;
         data.bank_name = formData.bankName;
         data.ifsc_code = formData.ifscCode;
         data.account_holder_name = formData.accountHolderName;
         data.monthly_salary_amount = formData.monthlyAmount;
      } else if (data.payroll === "Contractor") {
         data.contractor_master_id = formData.contractorName;
         data.driver_account_number = formData.accountNumber;
         data.driver_bank_name = formData.bankName;
         data.driver_ifsc_code = formData.ifscCode;
         data.driver_account_holder_name = formData.accountHolderName;
         data.driver_monthly_salary_amount = formData.monthlyAmount;
         data.monthly_salary_commission_amount = formData?.monthlyCommAmount;
      }

      if (!data.pan_card) {
         delete data.pan_card;
         delete data.pan_card_doc;
      }
      if (!data.aadhar_card) {
         delete data.aadhar_card;
         delete data.aadhar_card_doc;
      }

      const newData = new FormData();
      Object.keys(data).forEach((key) => {
         newData.append(key, data[key]);
      });

      dispatch(backdropLoadingAction(true));
      dispatch(
         addDriverMasterAsync({
            data: newData,
            err: (e) => {
               dispatch(backdropLoadingAction(false));
               toast.error(e.errors ? Object.values(e?.errors)?.[0].join() : e?.message);
            },
            done: () => {
               dispatch(backdropLoadingAction(false));
               toast.success("Record added successfully !");
               navigate("/driver-master/view");
            },
         })
      );
   };

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      dispatch(
         fetchAllContractorsAsync({
            done: () => {
               dispatch(backdropLoadingAction(false));
            },
            err: () => {
               dispatch(backdropLoadingAction(false));
            },
         })
      );
   }, []);

   const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: handleAddDriverMaster,
   });

   const {
      errors,
      touched,
      values,
      isSubmitting,
      getFieldProps,
      handleSubmit,
      setFieldValue,
   } = formik;

   // Focus error field
   useFocusErrorField(errors, isSubmitting);

   return (
      <FormikProvider value={formik}>
         <div>
            <BodyHeader title="Add Driver Master" />
            <form className="p-3 shadow-lg">
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Personal Details</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-3">
                           <CustomInput
                              require
                              label="Name"
                              id="driverName"
                              placeholder="Enter Name"
                              {...getFieldProps("name")}
                              errors={Boolean(touched.name) && Boolean(errors.name)}
                              message={errors.name}
                           />
                        </div>

                        <div className="col-lg-3">
                           <CustomInput
                              require
                              label="Mobile No"
                              id="mobile"
                              placeholder="Enter Mobile No"
                              {...getFieldProps("mobile")}
                              errors={Boolean(touched.mobile) && Boolean(errors.mobile)}
                              message={errors.mobile}
                           />
                        </div>

                        <div className="col-lg-3">
                           <CustomDatePicker
                              require
                              label="DOB"
                              name="dob"
                              id="dob"
                              placeholder="Select DOB"
                              {...getFieldProps("dob")}
                              errors={Boolean(touched.dob) && Boolean(errors.dob)}
                              message={errors.dob}
                           />
                        </div>

                        {/* Date of Joining */}
                        <div className="col-lg-3">
                           <CustomDatePicker
                              require
                              label="Date of Joining"
                              placeholder="Select Date of Joining"
                              id="joiningDate"
                              {...getFieldProps("joiningDate")}
                              errors={
                                 Boolean(touched.joiningDate) &&
                                 Boolean(errors.joiningDate)
                              }
                              message={errors.joiningDate}
                           />
                        </div>

                        {/* Date of Exit */}
                        <div className="col-lg-4">
                           <CustomDatePicker
                              label="Date of Exit"
                              placeholder="Select Date of Exit"
                              id="exitDate"
                              {...getFieldProps("exitDate")}
                              errors={
                                 Boolean(touched.exitDate) && Boolean(errors.exitDate)
                              }
                              message={errors.exitDate}
                           />
                        </div>

                        {/* Expierence (Years) */}
                        <div className="col-lg-4">
                           <CustomDropdown
                              require
                              optionData={YRS_OF_EXPERIENCE_DATA}
                              label="Experience (Years)"
                              placeholder="Select Experience (Years)"
                              id="expofyears"
                              {...getFieldProps("expofyears")}
                              onChange={(values) => {
                                 setFieldValue("expofyears", values?.[0]?.value);
                              }}
                              errors={
                                 Boolean(touched.expofyears) && Boolean(errors.expofyears)
                              }
                              message={errors.expofyears}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomFileUpload
                              label="Driver Image"
                              require={true}
                              id="driverImage"
                              name="driverImage"
                              accept={"image/png, image/jpg, image/jpeg, image/webp"}
                              {...getFieldProps("driverImage")}
                              onChange={(e) => {
                                 setFieldValue("driverImage", e.target.files[0]);
                              }}
                              errors={
                                 Boolean(touched.driverImage) &&
                                 Boolean(errors.driverImage)
                              }
                              message={errors.driverImage}
                           />
                        </div>

                        <div className="col-lg-12">
                           <CustomTextArea
                              label="Address"
                              id="address"
                              name="address"
                              placeholder="Enter Address"
                              {...getFieldProps("address")}
                              errors={Boolean(touched.address) && Boolean(errors.address)}
                              message={errors.address}
                           />
                        </div>
                     </div>
                  </div>
               </div>

               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Driver License</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        {/* Driver License */}
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Driver License"
                              id="driverLicense"
                              placeholder="Enter Driver License"
                              {...getFieldProps("drivingLicense")}
                              errors={
                                 Boolean(touched.drivingLicense) &&
                                 Boolean(errors.drivingLicense)
                              }
                              message={errors.drivingLicense}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomDatePicker
                              require
                              label="License Expiry Date"
                              placeholder="Select Expiry Date"
                              id="licenseExp"
                              {...getFieldProps("licenseExp")}
                              errors={
                                 Boolean(touched.licenseExp) && Boolean(errors.licenseExp)
                              }
                              message={errors.licenseExp}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomFileUpload
                              label="DL Document"
                              require={true}
                              id="dlUpload"
                              {...getFieldProps("dlUpload")}
                              accept=".pdf, .jpeg, .jpg, .png"
                              onChange={(e) => {
                                 setFieldValue("dlUpload", e.target.files[0]);
                              }}
                              errors={
                                 Boolean(touched.dlUpload) && Boolean(errors.dlUpload)
                              }
                              message={errors.dlUpload}
                           />
                        </div>
                     </div>
                  </div>
               </div>

               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Identification Documents</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-6">
                           <CustomInput
                              require
                              inputType={"number"}
                              label="Aadhar Card No"
                              id="aadharCard"
                              placeholder="Enter Aadhar Card No"
                              {...getFieldProps("aadharCard")}
                              errors={
                                 Boolean(touched.aadharCard) && Boolean(errors.aadharCard)
                              }
                              message={errors.aadharCard}
                           />
                        </div>

                        <div className="col-lg-6">
                           {/* <label className="text-bold">
                           Aadhar Card No<span className="text-danger">*</span>
                           </label> */}
                           <CustomFileUpload
                              label="Aadhar Card"
                              name="aadharCardImg"
                              require={true}
                              accept=".pdf, .jpeg, .jpg, .png"
                              {...getFieldProps("aadharCardImg")}
                              onChange={(e) => {
                                 setFieldValue("aadharCardImg", e.target.files[0]);
                              }}
                              errors={
                                 Boolean(touched.aadharCardImg) &&
                                 Boolean(errors.aadharCardImg)
                              }
                              message={errors.aadharCardImg}
                           />
                        </div>

                        <div className="col-lg-6">
                           <CustomInput
                              label="Pan Card No"
                              id="panCard"
                              placeholder="Enter Pan Card"
                              {...getFieldProps("panCard")}
                              errors={Boolean(touched.panCard) && Boolean(errors.panCard)}
                              message={errors.panCard}
                           />
                        </div>

                        <div className="col-lg-6">
                           <CustomFileUpload
                              name="panCardImg"
                              label="PAN Card"
                              accept=".pdf, .jpeg, .jpg, .png"
                              {...getFieldProps("panCardImg")}
                              onChange={(e) => {
                                 setFieldValue("panCardImg", e.target.files[0]);
                              }}
                              errors={
                                 Boolean(touched.panCardImg) && Boolean(errors.panCardImg)
                              }
                              message={errors.panCardImg}
                           />
                        </div>
                     </div>
                  </div>
               </div>

               {/* Pay roll Sec */}
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Pay roll</h3>
                  </div>

                  <div className="card-body">
                     <div className="form-group">
                        <CustomRadio
                           label="Eisaku Pay Roll"
                           id="eisakuPayRoll"
                           value="Eisaku Pay Roll"
                           defaultChecked={values.payRollType === "Eisaku Pay Roll"}
                           checked={values.payRollType === "Eisaku Pay Roll"}
                           onChange={(event) =>
                              setFieldValue("payRollType", event.target.value)
                           }
                           errors={
                              Boolean(touched.payRollType) && Boolean(errors.payRollType)
                           }
                           message={errors.payRollType}
                        />
                        <CustomRadio
                           label="Contractor"
                           id="contractor"
                           value="Contractor"
                           checked={values.payRollType === "Contractor"}
                           onChange={(event) =>
                              setFieldValue("payRollType", event.target.value)
                           }
                           errors={
                              Boolean(touched.payRollType) && Boolean(errors.payRollType)
                           }
                           message={errors.payRollType}
                        />
                     </div>
                     {values.payRollType === "Contractor" && (
                        <div className="row">
                           <div className="col-lg-6">
                              <CustomDropdown
                                 require
                                 label="Contractor Name"
                                 id="contractorName"
                                 optionData={contractorsData}
                                 placeholder="Enter contractor Name"
                                 onChange={(values) => {
                                    setFieldValue("contractorName", values?.[0]?.value);
                                 }}
                                 errors={
                                    Boolean(touched.contractorName) &&
                                    Boolean(errors.contractorName)
                                 }
                                 message={errors.contractorName}
                              />
                           </div>
                           <div className="col-lg-6">
                              <CustomInput
                                 require
                                 label="Monthly Commission Amount"
                                 id="monthlyCommAmount"
                                 inputType={"number"}
                                 placeholder="Enter monthly commission amount"
                                 {...getFieldProps("monthlyCommAmount")}
                                 errors={
                                    Boolean(touched.monthlyCommAmount) &&
                                    Boolean(errors.monthlyCommAmount)
                                 }
                                 message={errors.monthlyCommAmount}
                              />
                           </div>
                        </div>
                     )}
                     <div className="row">
                        <div className="col-lg-4">
                           <CustomInput
                              require={values.payRollType === "Eisaku Pay Roll"}
                              label={`${
                                 values.payRollType === "Contractor" ? "Driver" : ""
                              } Account Number`}
                              id="accountNumber"
                              placeholder="Enter Account Number"
                              {...getFieldProps("accountNumber")}
                              errors={
                                 Boolean(touched.accountNumber) &&
                                 Boolean(errors.accountNumber)
                              }
                              message={errors.accountNumber}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require={values.payRollType === "Eisaku Pay Roll"}
                              label={`${
                                 values.payRollType === "Contractor" ? "Driver" : ""
                              } Bank name`}
                              id="bankName"
                              placeholder="Enter Bank Name"
                              {...getFieldProps("bankName")}
                              errors={
                                 Boolean(touched.bankName) && Boolean(errors.bankName)
                              }
                              message={errors.bankName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require={values.payRollType === "Eisaku Pay Roll"}
                              label={`${
                                 values.payRollType === "Contractor" ? "Driver" : ""
                              } IFSC Code`}
                              id="ifscCode"
                              placeholder="Enter IFSC Code"
                              {...getFieldProps("ifscCode")}
                              errors={
                                 Boolean(touched.ifscCode) && Boolean(errors.ifscCode)
                              }
                              message={errors.ifscCode}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomInput
                              require={values.payRollType === "Eisaku Pay Roll"}
                              label={`Account Holder Name ${
                                 values.payRollType === "Contractor" ? "(Driver)" : ""
                              }`}
                              id="accountHolderName"
                              placeholder="Enter Account Holder name"
                              {...getFieldProps("accountHolderName")}
                              errors={
                                 Boolean(touched.accountHolderName) &&
                                 Boolean(errors.accountHolderName)
                              }
                              message={errors.accountHolderName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require={values.payRollType === "Eisaku Pay Roll"}
                              label={`${
                                 values.payRollType === "Contractor" ? "Driver" : ""
                              } Monthly salary amount`}
                              id="monthlyAmount"
                              inputType={"number"}
                              placeholder="Enter Monthly salary amount"
                              {...getFieldProps("monthlyAmount")}
                              errors={
                                 Boolean(touched.monthlyAmount) &&
                                 Boolean(errors.monthlyAmount)
                              }
                              message={errors.monthlyAmount}
                           />
                        </div>
                     </div>
                     {values.payRollType === "Eisaku Pay roll" && <></>}
                  </div>
               </div>
               <div className="col-12 mt-4 text-center">
                  <button
                     className="btn btn-primary px-4 py-3"
                     type="button"
                     onClick={handleSubmit}
                  >
                     <h6 className="mb-0 text-uppercase">Submit</h6>
                  </button>
               </div>
            </form>
         </div>
      </FormikProvider>
   );
}
